import React, { useCallback, useState } from 'react';
import Loading from 'components/Loading/index';
import { isAndroid, isIOS } from 'react-device-detect';
import { hideProgressWeb, showProgressWeb } from 'common/appInterface';

type Props = {
  children: React.ReactNode;
};

type Context = {
  showLoading: () => void;
  hideLoading: () => void;
  setAppVersion: React.Dispatch<React.SetStateAction<number | undefined>>;
};

const LoadingContext = React.createContext<Context | undefined>(undefined);

const LoadingProvider: React.FC<Props> = ({ children }) => {
  const iosProgressAppVersion = 770;

  const [appVersion, setAppVersion] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  const showLoading = useCallback(() => {
    if (isIOS && window.webkit) {
      if (appVersion && appVersion < iosProgressAppVersion) setLoading(true);
      else showProgressWeb();
    } else if (isAndroid && window.appInterface) {
      showProgressWeb();
    } else {
      setLoading(true);
    }
  }, [appVersion]);

  const hideLoading = useCallback(() => {
    if (isIOS && window.webkit) {
      if (appVersion && appVersion < iosProgressAppVersion) setLoading(false);
      else hideProgressWeb();
    } else if (isAndroid && window.appInterface) {
      hideProgressWeb();
    } else {
      setLoading(false);
    }
  }, [appVersion]);

  return (
    <LoadingContext.Provider
      value={{
        showLoading,
        hideLoading,
        setAppVersion
      }}>
      <Loading isLoading={loading} />
      {children}
    </LoadingContext.Provider>
  );
};

const useLoading = () => {
  const context = React.useContext<Context | undefined>(LoadingContext);
  if (context === undefined) {
    throw new Error('useLoading');
  }
  return context;
};

export { useLoading };
export default LoadingProvider;
